/**
 * Keyboard Shortcuts Dialog
 *
 * Displays all available keyboard shortcuts in a modal dialog.
 * Triggered by Shift+? shortcut.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  formatShortcut,
  groupShortcutsByCategory,
  isMac,
  type Shortcut,
  type ShortcutGroup,
} from '@/hooks/use-keyboard-shortcuts';
import { overlayVariants, prefersReducedMotion, scaleVariants } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { Icon } from './icon';

// =============================================================================
// Types
// =============================================================================

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shortcuts: Shortcut[];
  /** Names the set of shortcuts when a screen brings its own, such as the test runner.
   * The category labels below it are not translated, so a screen-specific set needs a
   * heading of its own to say, in the reader's language, what it belongs to. */
  heading?: string;
}

// =============================================================================
// Kbd Component - Styled keyboard key
// =============================================================================

function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded border px-1.5',
        'border-border bg-muted font-mono text-[11px] font-medium text-muted-foreground',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

// =============================================================================
// Shortcut Row Component
// =============================================================================

function ShortcutRow({ shortcut }: { shortcut: Shortcut }) {
  const formatted = formatShortcut(shortcut);
  const parts = formatted.split(/(\+| then )/);

  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-foreground">{shortcut.description}</span>
      <div className="flex items-center gap-1">
        {parts.map((part, i) => {
          if (part === '+') {
            return (
              <span key={i} className="text-xs text-muted-foreground">
                +
              </span>
            );
          }
          if (part === ' then ') {
            return (
              <span key={i} className="mx-1 text-xs text-muted-foreground">
                then
              </span>
            );
          }
          return <Kbd key={i}>{part}</Kbd>;
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Shortcut Group Component
// =============================================================================

function ShortcutGroupSection({ group }: { group: ShortcutGroup }) {
  return (
    <div className="space-y-1">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {group.category}
      </h3>
      <div className="divide-y divide-border">
        {group.shortcuts.map((shortcut) => (
          <ShortcutRow key={shortcut.id} shortcut={shortcut} />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Main Dialog Component
// =============================================================================

export function KeyboardShortcutsDialog({
  open,
  onOpenChange,
  shortcuts,
  heading,
}: KeyboardShortcutsDialogProps) {
  const { t } = useTranslation('common');
  const shouldAnimate = !prefersReducedMotion();
  const groups = groupShortcutsByCategory(shortcuts);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            {/* Overlay */}
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/50"
                variants={shouldAnimate ? overlayVariants : undefined}
                initial="initial"
                animate="animate"
                exit="exit"
              />
            </DialogPrimitive.Overlay>

            {/* Content */}
            <DialogPrimitive.Content asChild>
              <motion.div
                className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] rounded-xl border border-border bg-card p-0 shadow-xl"
                variants={shouldAnimate ? scaleVariants : undefined}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                      <Icon name="keyboard" className="h-4 w-4 text-primary" />
                    </div>
                    <DialogPrimitive.Title className="text-lg font-semibold">
                      {t('shortcuts.title', 'Keyboard Shortcuts')}
                    </DialogPrimitive.Title>
                  </div>
                  <DialogPrimitive.Close className="rounded-md p-1.5 hover:bg-accent transition-colors">
                    <Icon name="close" className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </DialogPrimitive.Close>
                </div>

                {/* Body */}
                <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
                  {heading && <p className="mb-4 text-sm font-medium text-foreground">{heading}</p>}
                  <div className="space-y-6">
                    {groups.map((group) => (
                      <ShortcutGroupSection key={group.category} group={group} />
                    ))}
                  </div>

                  {/* Platform hint */}
                  <div className="mt-6 rounded-lg bg-muted/50 px-4 py-3">
                    <p className="text-xs text-muted-foreground">
                      {isMac() ? (
                        <>
                          <Kbd>⌘</Kbd> is the Command key on your Mac keyboard
                        </>
                      ) : (
                        <>
                          <Kbd>Ctrl</Kbd> is the Control key on your keyboard
                        </>
                      )}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-border px-6 py-3">
                  <p className="text-center text-xs text-muted-foreground">
                    {t('shortcuts.hint', 'Press')} <Kbd>?</Kbd>{' '}
                    {t('shortcuts.hintSuffix', 'anytime to show this dialog')}
                  </p>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
